import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import {
	Alert,
	Button,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	Radio,
	Row,
	Select,
	Table,
	Typography,
	Upload,
	UploadFile,
} from 'antd';
import {
	DeleteFilled,
	DeleteOutlined,
	EditOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import { FC, Fragment } from 'react';
import TextArea from 'antd/es/input/TextArea';
import ShareholderModal, {
	useShareholderModal,
} from './modals/ShareholderModal';
import ManagerModal, { useManagerModal } from './modals/ManagerModal';
import GeneralInfoModal, {
	useGeneralInfoModalModal,
} from './modals/GroupInfoModal';
import ManagerDetailModal, {
	useManagerDetailModal,
} from './modals/ManagerDetailModal';
import ShareholderDetailModal, {
	useShareholderDetailModal,
} from './modals/ShareholderDetailModal';
import UploadModal, { useUploadModal } from './modals/UploadModal';
import {
	getNAPCompanyInformation,
	NAPCompanyInformationPayload,
	NAPState,
	submitNAPCompanyInformation,
	verifyNAPCompanyInformation,
} from '@/libs/fleet-api/services/nap';
import { useMutation, useQuery } from '@tanstack/react-query';
import { LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useForm } from '@/libs/antd/form';
import rule from '@/libs/antd/rules';
import { FileData } from '@/libs/fleet-api/services/types';
import { uploader } from '@/libs/fleet-api/uploader';
import {
	CustomerCompanyManagementInformationForm,
	CustomerCompanyShareholderInformationForm,
} from '../frm/customer-v2/CustomerPage';
import { useModals } from '@/components/modals/ModalProvider';
import { useUserData } from '@/libs/fleet-api/hooks';
import { MasterData } from '@/libs/fleet-api/services/master-data';
import CompanyManagerModal, {
	useCompanyManagerModal,
} from './modals/CompanyManagerModal';
import CompanyShareholderModal, {
	useCompanyShareholderModal,
} from './modals/CompanyShareholderModal';
import { formatCurrency } from '@/libs/antd/formater';
import { useSelectIndustry } from '@/libs/fleet-api/hooks/master-data';

export type NAPCompanyGroupManagerForm = Omit<
	NAPCompanyInformationPayload['companyGroups'][number]['managementStructure'][number],
	'position'
> & {
	position: MasterData;
	isShareholder: boolean;
	shareholderId: number;
};

export type NAPCompanyGroupShareholderForm = Omit<
	NAPCompanyInformationPayload['companyGroups'][number]['shareholderStructure'][number],
	'position'
> & {
	position: MasterData;
	isManagement: boolean;
	managementId?: number;
};

type NAPCompanyGroupForm = Omit<
	NAPCompanyInformationPayload,
	'companyManagements' | 'companyShareholders' | 'companyGroups'
> & {
	companyManagements: CustomerCompanyManagementInformationForm[];
	companyShareholders: CustomerCompanyShareholderInformationForm[];
	companyGroups: {
		isDefault: boolean;
		generalInformation: Omit<
			NAPCompanyInformationPayload['companyGroups'][number]['generalInformation'],
			'industrySectorId'
		> & {
			industrySector: MasterData;
		};
		managementStructure: NAPCompanyGroupManagerForm[];
		shareholderStructure: NAPCompanyGroupShareholderForm[];
	}[];
};

const NAPCompanyGroup: FC<{
	napState?: NAPState;
	prospectId: string;
	customerId: string;
	onSubmitted: () => void;
}> = ({ napState, prospectId, customerId, onSubmitted }) => {
	const { modal } = useModals();
	const { form, value, fieldName } = useForm<NAPCompanyGroupForm>();
	// value?.companyManagements; // Unused expression removed

	const managerModal = useManagerModal((type, payload) => {
		if (type === 'edit') {
			form.setFieldValue(
				fieldName('companyManagements'),
				value?.companyManagements?.map((data, index) => {
					return index === payload.index ? payload : data;
				}),
			);
			if (payload.isShareholder) {
				const foundShareholderIndex = value?.companyShareholders?.findIndex(
					(data) => data.id === payload.shareholderId,
				);

				if (foundShareholderIndex !== -1) {
					form.setFieldValue(
						fieldName('companyShareholders'),
						value?.companyShareholders?.map((data, index) => {
							return index === foundShareholderIndex
								? {
										...data,
										name: payload.name,
										citizenship: payload.citizenship,
										nationality: payload.nationality,
										identityCardType: payload.identityCardType,
										identityNumber: payload.identityNumber,
										dateOfBirth: payload.dateOfBirth,
										address: payload.address,
										rt: payload.rt,
										rw: payload.rw,
										phoneNumber: payload.phoneNumber,
										postal: payload.postal,
										shareholding: payload.shareholding,
									}
								: data;
						}),
					);
				}
			}
		} else {
			form.setFieldValue(fieldName('companyManagements'), [
				...(value?.companyManagements ?? []),
				payload,
			]);
		}
	});

	const shareholderModal = useShareholderModal((type, payload) => {
		if (type === 'edit') {
			form.setFieldValue(
				fieldName('companyShareholders'),
				value?.companyShareholders?.map((data, index) => {
					return index === payload.index ? payload : data;
				}),
			);
			if (payload.isManagement) {
				const foundManagerIndex = value?.companyManagements?.findIndex(
					(data) => data.shareholderId === payload.id,
				);

				if (foundManagerIndex !== -1) {
					form.setFieldValue(
						fieldName('companyManagements'),
						value?.companyManagements?.map((data, index) => {
							return index === foundManagerIndex
								? {
										...data,
										name: payload.name,
										citizenship: payload.citizenship,
										nationality: payload.nationality,
										identityCardType: payload.identityCardType,
										identityNumber: payload.identityNumber,
										address: payload.address,
										rt: payload.rt,
										rw: payload.rw,
										postal: payload.postal,
										phoneNumber: payload.phoneNumber,
										shareholding: payload.shareholding,
									}
								: data;
						}),
					);
				}
			}
		} else {
			form.setFieldValue(fieldName('companyShareholders'), [
				...(value?.companyShareholders ?? []),
				payload,
			]);
		}
	});

	const generalInfoModalModal = useGeneralInfoModalModal((type, payload) => {
		if (type === 'edit') {
			form.setFieldValue(
				fieldName('companyGroups'),
				value?.companyGroups?.map((data, index) => {
					return index === payload.index ? payload : data;
				}),
			);
		} else {
			form.setFieldValue(fieldName('companyGroups'), [
				...(value?.companyGroups ?? []),
				payload,
			]);
		}
	});

	const managerDetailModal = useManagerDetailModal();
	const shareholderDetailModal = useShareholderDetailModal();

	const companyManagerModal = useCompanyManagerModal();
	const companyShareholderModal = useCompanyShareholderModal();

	const uplodaModal = useUploadModal((values) => {
		if (values.type === 'management') {
			form.setFieldValue(
				fieldName('latestManagementDeedDocument'),
				values.data,
			);
		}

		if (values.type === 'shareholder') {
			form.setFieldValue(
				fieldName('latestShareholderDeedDocument'),
				values.data,
			);
		}
	});

	const getNAPCompanyInfoQuery = useQuery({
		queryKey: ['getNAPCompanyInformation', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getNAPCompanyInformation(id);
		},
	});

	const selectIndustry = useSelectIndustry();

	const { data } = getNAPCompanyInfoQuery.data ?? {};

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const submitNAPCompanyInfoMutation = useMutation({
		mutationFn: submitNAPCompanyInformation,
	});

	const verifyNAPCompanyInfoMutation = useMutation({
		mutationFn: verifyNAPCompanyInformation,
	});

	const { groupName } = useUserData() ?? {};
	const isReadOnly =
		napState?.action === 'VIEW' || napState?.action === 'DISABLED';

	if (getNAPCompanyInfoQuery.isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	const renderCompanyInfo = () => {
		return (
			<Fragment>
				<Typography.Title level={3}>Informasi Umum Perusahaan</Typography.Title>

				<Row gutter={40}>
					<Col span={12}>
						<Form.Item label="Nama Debitur">
							<Input
								value={data?.debtorName}
								disabled
							/>
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				<Row gutter={40}>
					<Col span={24}>
						<Form.Item
							label="Alamat"
							labelCol={{ span: 4 }}
							wrapperCol={{ span: 20, style: { marginInline: -10 } }}
						>
							<Input
								value={data?.address}
								disabled
							/>
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				<Row gutter={40}>
					<Col span={12}>
						<Form.Item label="RT/RW">
							<Row
								wrap={false}
								gutter={10}
								align={'middle'}
							>
								<Col flex="auto">
									<Input
										value={data?.rt}
										disabled
									/>
								</Col>
								<Col flex="none">-</Col>
								<Col flex="auto">
									<Input
										value={data?.rw}
										disabled
									/>
								</Col>
							</Row>
						</Form.Item>
					</Col>

					<Col span={12}>
						<Form.Item label="Kode Pos">
							<Input
								value={data?.postal?.postalCode}
								disabled
							/>
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				<Row gutter={40}>
					<Col span={12}>
						<Form.Item label="Kelurahan">
							<Input
								value={data?.postal?.locationName}
								disabled
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label="Kecamatan">
							<Input
								value={data?.postal?.districtName}
								disabled
							/>
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				<Row gutter={40}>
					<Col span={12}>
						<Form.Item label="Kota">
							<Input
								value={data?.postal?.cityName}
								disabled
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label="Bidang Usaha">
							<Input
								value={data?.industrySector}
								disabled
							/>
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				<Row gutter={40}>
					<Col span={12}>
						<Form.Item label="Key Person">
							<Input
								value={data?.keyPerson}
								disabled
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label="Jabatan">
							<Input
								value={data?.position?.value}
								disabled
							/>
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				<Row gutter={40}>
					<Col span={12}>
						<Form.Item label="Tanggal Berdiri Perusahaan">
							<DatePicker
								disabled
								value={
									data?.companyEstablishmentDate &&
									dayjs(data?.companyEstablishmentDate)
								}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('relationshipYear')}
							initialValue={data?.relationshipYear}
							label="Tahun Berhubungan dengan MTF/BMRI"
							getValueProps={(value) => ({
								value: value && dayjs(value),
							})}
							normalize={(value) => value && dayjs(value).year().toString()}
							rules={[rule.required()]}
						>
							<DatePicker.YearPicker disabled={isReadOnly} />
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				<Row gutter={40}>
					<Col span={12}>
						<Form.Item
							name={fieldName('financedSectorBusinessStart')}
							initialValue={data?.financedSectorBusinessStart}
							label="Mulai Usaha untuk Sektor yang Dibiayai"
							getValueProps={(value) => ({ value: value && dayjs(value) })}
							normalize={(value) =>
								value && dayjs(value).utc(true).toISOString()
							}
							rules={[rule.required()]}
						>
							<DatePicker disabled={isReadOnly} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('numberOfEmployees')}
							initialValue={data?.numberOfEmployees}
							label="Jumlah Karyawan"
							rules={[rule.required()]}
						>
							<InputNumber disabled={isReadOnly} />
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				<Form.Item
					label="BMPP"
					name={fieldName('bmppId')}
					initialValue={data?.bmpp?.id}
					labelCol={{ span: 4 }}
					wrapperCol={{ span: 20, style: { marginInline: -10 } }}
					rules={[rule.required()]}
				>
					<Radio.Group>
						<Row gutter={20}>
							{data?.bmppOptions?.map((data) => (
								<Col key={data.id}>
									<Radio
										value={data.id}
										disabled={isReadOnly}
									>
										{data.value}
									</Radio>
								</Col>
							))}
						</Row>
					</Radio.Group>
				</Form.Item>

				<Divider />
				{renderManagers()}
				{renderShareholders()}
			</Fragment>
		);
	};

	const renderManagers = () => {
		return (
			<Fragment>
				<FleetTitle>Data Susunan Pengurus</FleetTitle>
				<Row
					gutter={10}
					style={{ marginTop: 20 }}
				>
					<Col flex="none">
						<Form.Item
							name={fieldName('latestManagementDeedNumber')}
							initialValue={data?.latestManagementDeedNumber}
							label={
								data?.businessEntity.id === 1
									? 'Berdasarkan Akta No'
									: 'Berdasarkan Akta Perubahan Pengurus dan Pemegang Saham Terakhir No.'
							}
							labelCol={{ flex: 'none' }}
							wrapperCol={{ flex: 'none', style: { width: 60 } }}
							rules={
								value?.latestManagementDeedDocument ? [rule.required()] : []
							}
						>
							<InputNumber
								disabled={isReadOnly || !value?.latestManagementDeedDocument}
								controls={false}
							/>
						</Form.Item>
					</Col>
					<Col flex="none">
						<Form.Item
							name={fieldName('latestManagementDeedYear')}
							initialValue={data?.latestManagementDeedYear}
							label="Tahun"
							labelCol={{ flex: 'none' }}
							wrapperCol={{ flex: 'none' }}
							getValueProps={(value) => ({
								value: value && dayjs(value),
							})}
							normalize={(value) => value && dayjs(value).year().toString()}
							rules={
								value?.latestManagementDeedDocument ? [rule.required()] : []
							}
						>
							<DatePicker.YearPicker
								disabled={isReadOnly || !value?.latestManagementDeedDocument}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				<Alert
					type="info"
					showIcon
					message={
						<strong style={{ color: '#E10101' }}>
							{data?.businessEntity.id === 1
								? 'Silakan Upload Dokumen Akta Perubahan Pengurus Terakhir apabila mengalami perubahan'
								: 'Silakan Upload Dokumen Akta Perubahan Pengurus dan Pemegang Saham Terakhir apabila mengalami perubahan'}
						</strong>
					}
					action={
						<Button
							style={{
								borderRadius: 50,
								fontSize: 12,
								fontWeight: 700,
								height: 24,
								...(!isReadOnly && {
									color: '#1557BC',
									borderColor: '#1557BC',
								}),
							}}
							disabled={isReadOnly}
							onClick={() => {
								uplodaModal.open({
									type: 'management',
									data: data?.latestManagementDeedDocument,
								});
							}}
						>
							Update
						</Button>
					}
				/>

				<Divider />

				<Row style={{ marginBottom: 20 }}>
					<Col flex="auto" />
					<Col flex="none">
						<Button
							style={{
								borderRadius: 50,
								fontSize: 12,
								height: 24,
								fontWeight: 700,
								...(!isReadOnly &&
									value?.latestManagementDeedDocument && {
										borderColor: '#1557BC',
										color: '#1557BC',
									}),
							}}
							icon={'+'}
							disabled={isReadOnly || !value?.latestManagementDeedDocument}
							onClick={() => {
								managerModal.open({
									type: 'new',
								});
							}}
						>
							Add Another
						</Button>
					</Col>
				</Row>

				<Table
					scroll={{ x: 'max-content' }}
					bordered
					pagination={false}
					dataSource={
						value?.companyManagements?.map((data, i) => {
							return {
								...data,
								key: i + 1,
							};
						}) ?? []
					}
					columns={[
						{
							title: 'Action',
							colSpan: 2,
							width: 100,
							align: 'center',
							render: (_, record, index) => {
								const isDisabled =
									isReadOnly || !value?.latestManagementDeedDocument;

								return (
									<a
										style={isDisabled ? { color: '#bababa' } : {}}
										onClick={(e) => {
											e.preventDefault();
											if (!isDisabled) {
												managerModal.open({
													type: 'edit',
													data: {
														...record,
														index,
													},
												});
											}
										}}
									>
										<EditOutlined />
									</a>
								);
							},
						},
						{
							colSpan: 0,
							width: 100,
							align: 'center',
							render: (_, __, index) => {
								const isDisabled =
									isReadOnly || !value?.latestManagementDeedDocument;

								return (
									<a
										style={isDisabled ? { color: '#bababa' } : {}}
										onClick={(e) => {
											e.preventDefault();
											if (!isDisabled) {
												form.setFieldValue(
													fieldName('companyManagements'),
													value?.companyManagements?.filter(
														(_, i) => index !== i,
													),
												);
											}
										}}
									>
										<DeleteOutlined />
									</a>
								);
							},
						},
						{
							title: 'Nama',
							render: (_, record) => record.name,
						},
						{
							title: 'Kewarganegaraan',
							render: (_, record) => record.citizenship?.value,
						},
						{
							title: 'Nationality',
							render: (_, record) =>
								record.nationality?.value || record.nationality?.name || '-',
						},
						{
							title: 'Jenis Identitas',
							render: (_, record) => record.identityCardType?.value,
						},
						{
							title: 'No. ID',
							render: (_, record) => record.identityNumber,
						},
						{
							title: 'Tanggal Lahir',
							render: (_, record) =>
								record.dateOfBirth &&
								dayjs(record.dateOfBirth).format('DD/MM/YYYY'),
						},
						{
							title: 'Jabatan',
							render: (_, record) => record.positionId?.value ?? '-',
						},
						{
							title: 'Alamat Perusahaan',
							render: (_, record) => record.address,
						},
						{
							title: 'Kode Pos',
							render: (_, record) => record.postal?.postalCode,
						},
						{
							title: 'Kota',
							render: (_, record) => record.postal?.cityName,
						},
						{
							title: 'Kecamatan',
							render: (_, record) => record.postal?.districtName,
						},
						{
							title: 'Kelurahan',
							render: (_, record) => record.postal?.locationName,
						},
						{
							title: 'RT/RW',
							render: (_, record) => `${record.rt}/${record.rw}`,
						},
						{
							title: 'No Telepon',
							render: (_, record) => record.phoneNumber,
						},
						{
							title: 'Status',
							render: (_, record) => record.status.value,
						},
					]}
				/>
				<Divider />
			</Fragment>
		);
	};

	const renderShareholders = () => {
		return (
			<Fragment>
				<FleetTitle>Data Kepengurusan Saham</FleetTitle>
				{data?.businessEntity.id === 1 && (
					<Fragment>
						<Row
							gutter={10}
							style={{ marginTop: 20 }}
						>
							<Col flex="none">
								<Form.Item
									name={fieldName('latestShareholderDeedNumber')}
									initialValue={data?.latestShareholderDeedNumber}
									label="Berdasarkan Akta No"
									labelCol={{ flex: 'none' }}
									wrapperCol={{ flex: 'none', style: { width: 60 } }}
									rules={
										value?.latestShareholderDeedDocument
											? [rule.required()]
											: []
									}
								>
									<InputNumber
										disabled={
											isReadOnly || !value?.latestShareholderDeedDocument
										}
										controls={false}
									/>
								</Form.Item>
							</Col>
							<Col flex="none">
								<Form.Item
									name={fieldName('latestShareholderDeedYear')}
									initialValue={data?.latestShareholderDeedYear}
									label="Tahun"
									labelCol={{ flex: 'none' }}
									wrapperCol={{ flex: 'none' }}
									getValueProps={(value) => ({
										value: value && dayjs(value),
									})}
									normalize={(value) => value && dayjs(value).year().toString()}
									rules={
										value?.latestShareholderDeedDocument
											? [rule.required()]
											: []
									}
								>
									<DatePicker.YearPicker
										disabled={
											isReadOnly || !value?.latestShareholderDeedDocument
										}
									/>
								</Form.Item>
							</Col>
						</Row>

						<Divider />

						<Alert
							type="info"
							showIcon
							message={
								<strong style={{ color: '#E10101' }}>
									Silakan Upload Dokumen Akta Perubahan Pemegang Saham Terakhir
									apabila mengalami perubahan
								</strong>
							}
							action={
								<Button
									style={{
										borderRadius: 50,
										fontSize: 12,
										fontWeight: 700,
										height: 24,
										...(!isReadOnly && {
											color: '#1557BC',
											borderColor: '#1557BC',
										}),
									}}
									disabled={isReadOnly}
									onClick={() => {
										uplodaModal.open({
											type: 'shareholder',
											data: data?.latestShareholderDeedDocument,
										});
									}}
								>
									Update
								</Button>
							}
						/>

						<Divider />
					</Fragment>
				)}

				<Row style={{ marginBlock: 20 }}>
					<Col flex="auto" />
					<Col flex="none">
						<Button
							style={{
								borderRadius: 50,
								fontSize: 12,
								height: 24,
								fontWeight: 700,
								...((!isReadOnly && data?.businessEntity.id === 1
									? value?.latestShareholderDeedDocument
									: value?.latestManagementDeedDocument) && {
									borderColor: '#1557BC',
									color: '#1557BC',
								}),
							}}
							icon={'+'}
							disabled={
								isReadOnly ||
								(data?.businessEntity.id === 1
									? !value?.latestShareholderDeedDocument
									: !value?.latestManagementDeedDocument)
							}
							onClick={() => shareholderModal.open({ type: 'new' })}
						>
							Add Another
						</Button>
					</Col>
				</Row>

				<Table
					scroll={{ x: 'max-content' }}
					bordered
					pagination={false}
					dataSource={
						value?.companyShareholders?.map((data, i) => {
							return {
								...data,
								key: i + 1,
							};
						}) ?? []
					}
					columns={[
						{
							title: 'Action',
							colSpan: 2,
							width: 100,
							align: 'center',
							render: (_, record, index) => {
								const isDisabled =
									isReadOnly ||
									(data?.businessEntity.id === 1
										? !value?.latestShareholderDeedDocument
										: !value?.latestManagementDeedDocument);

								return (
									<a
										style={isDisabled ? { color: '#bababa' } : {}}
										onClick={(e) => {
											e.preventDefault();
											if (!isDisabled) {
												shareholderModal.open({
													type: 'edit',
													data: {
														...record,
														index,
													},
												});
											}
										}}
									>
										<EditOutlined />
									</a>
								);
							},
						},
						{
							colSpan: 0,
							width: 100,
							align: 'center',
							render: (_, __, index) => {
								const isDisabled =
									isReadOnly ||
									(data?.businessEntity.id === 1
										? !value?.latestShareholderDeedDocument
										: !value?.latestManagementDeedDocument);

								return (
									<a
										style={isDisabled ? { color: '#bababa' } : {}}
										onClick={(e) => {
											e.preventDefault();
											if (!isDisabled) {
												form.setFieldValue(
													fieldName('companyShareholders'),
													value?.companyShareholders?.filter(
														(_, i) => index !== i,
													),
												);
											}
										}}
									>
										<DeleteOutlined />
									</a>
								);
							},
						},
						{
							title: 'Nama',
							render: (_, record) => record.name,
						},
						{
							title: 'Kewarganegaraan',
							render: (_, record) => record.citizenship?.value,
						},
						{
							title: 'Nationality',
							render: (_, record) => record.nationality?.value ?? '-',
						},
						{
							title: 'Jenis Identitas',
							render: (_, record) => record.identityCardType?.value,
						},
						{
							title: 'No. ID',
							render: (_, record) => record.identityNumber,
						},
						// {
						// 	title: 'Tanggal Lahir',
						// 	render: () => '-',
						// },
						// {
						// 	title: 'Jabatan',
						// 	render: () => '-',
						// },
						{
							title: 'Alamat Perusahaan',
							render: (_, record) => record.address,
						},
						{
							title: 'Kode Pos',
							render: (_, record) => record.postal?.postalCode,
						},
						{
							title: 'Kota',
							render: (_, record) => record.postal?.cityName,
						},
						{
							title: 'Kecamatan',
							render: (_, record) => record.postal?.districtName,
						},
						{
							title: 'Kelurahan',
							render: (_, record) => record.postal?.locationName,
						},
						{
							title: 'RT/RW',
							render: (_, record) => `${record.rt}/${record.rw}`,
						},
						{
							title: 'No Telepon',
							render: (_, record) => record.phoneNumber,
						},
						{
							title: 'Kepemilikan Saham (%)',
							render: (_, record) => `${record.shareholding}%`,
						},
					]}
				/>
				<Divider />
			</Fragment>
		);
	};

	const renderGeneralGroup = () => {
		return (
			<Fragment>
				<Typography.Title level={3}>Informasi Umum Group</Typography.Title>

				<Form.List
					name={'companyGroups'}
					initialValue={
						(data?.companyGroups?.map((data) => {
							const generalInformation =
								data.companyGroupGeneralInformation ?? {};
							return {
								generalInformation: {
									id: generalInformation.id,
									entityName: generalInformation.entityName,
									keyPerson: generalInformation.keyPerson,
									relationshipYear: generalInformation.relationshipYear,
									industrySector: generalInformation.industrySector,
								},
								managementStructure: data.managementStructures?.map((data) => {
									return {
										id: data.id,
										name: data.name,
										position: data.positionId,
										shareAmount: data.shareAmount,
										shareValue: data.shareValue,
										shareholding: data.shareholding,
										isShareholder: data.isShareholder,
										shareholderId: data.shareholderId,
									};
								}),
								shareholderStructure: data.shareholderStructures?.map(
									(data) => {
										return {
											id: data.id,
											name: data.name,
											position: data.positionId,
											shareAmount: data.shareAmount,
											shareValue: data.shareValue,
											shareholding: data.shareholding,
											isManagement: data.isManagement,
											managementId: data.managementId,
										};
									},
								),
								isDefault: data.isDefault ?? false,
							};
						}) as NAPCompanyGroupForm['companyGroups']) ?? []
					}
				>
					{(fields, control) => {
						return (
							<Fragment>
								{fields.map((field, index) => {
									const number = index + 1;
									return (
										<Fragment key={field.key}>
											<FleetTitle level={3}>
												<Row>
													<Col flex="auto">Group Usaha {number}</Col>
													<Col flex="none">
														<Button
															type="primary"
															size="small"
															shape="circle"
															icon={<DeleteFilled />}
															style={{ height: 24 }}
															disabled={
																isReadOnly ||
																(value?.companyGroups?.[index]?.isDefault ??
																	false)
															}
															onClick={async () => {
																const isConfirmed = await modal.confirm({
																	title:
																		'Apakah Anda yakin ingin menghapus data Group Usaha?',
																	okText: 'YA',
																	cancelText: 'TIDAK',
																});
																if (isConfirmed) control.remove(index);
															}}
														/>
													</Col>
												</Row>
											</FleetTitle>

											<Fragment>
												<Form.Item
													name={[field.name, 'generalInformation', 'id']}
													noStyle
												/>
												<Form.Item
													name={[field.name, 'isDefault']}
													noStyle
												/>
												<Row
													gutter={40}
													style={{ marginTop: 20 }}
												>
													<Col span={12}>
														<Form.Item
															name={[
																field.name,
																'generalInformation',
																'entityName',
															]}
															label="Nama Entitas"
															rules={[rule.required()]}
														>
															<Input
																disabled={
																	(isReadOnly ||
																		value?.companyGroups?.[index]?.isDefault) ??
																	false
																}
															/>
														</Form.Item>
													</Col>
													<Col span={12}>
														<Form.Item
															name={[
																field.name,
																'generalInformation',
																'industrySector',
															]}
															label="Bidang Usaha"
															rules={[rule.required()]}
															getValueFromEvent={(_, option) => {
																return {
																	id: option?.value,
																	value: option?.label,
																};
															}}
															getValueProps={(value) => {
																if (!value) return {};
																return {
																	value: {
																		value: value.id,
																		label: value.value,
																	},
																};
															}}
														>
															<Select
																{...selectIndustry}
																disabled={isReadOnly}
															/>
														</Form.Item>
													</Col>
												</Row>
												<Divider />
												<Row gutter={40}>
													<Col span={12}>
														<Form.Item
															name={[
																field.name,
																'generalInformation',
																'relationshipYear',
															]}
															label="Tahun Berhubungan dengan MTF/BMRI"
															rules={[rule.required()]}
															getValueProps={(value) => ({
																value: value && dayjs(value),
															})}
															normalize={(value) =>
																value && dayjs(value).year().toString()
															}
														>
															<DatePicker.YearPicker disabled={isReadOnly} />
														</Form.Item>
													</Col>
													<Col span={12}>
														<Form.Item
															name={[
																field.name,
																'generalInformation',
																'keyPerson',
															]}
															label="Key Person"
															rules={[rule.required()]}
														>
															<Input disabled={isReadOnly} />
														</Form.Item>
													</Col>
												</Row>
												<Divider />
											</Fragment>

											<Fragment>
												<Row style={{ marginBottom: 20 }}>
													<Col flex="auto">
														<Typography.Title level={3}>
															Susunan Pengurus
														</Typography.Title>
													</Col>
													<Col flex="none">
														<Button
															style={{
																borderRadius: 50,
																fontSize: 12,
																height: 24,
																fontWeight: 700,
																...(!isReadOnly && {
																	borderColor: '#1557BC',
																	color: '#1557BC',
																}),
															}}
															icon={'+'}
															disabled={isReadOnly}
															onClick={async () => {
																const data = await companyManagerModal.open();
																if (!data) return;

																const newManagementId = -1 - Date.now();
																const newShareholderId = -2 - Date.now();

																form.setFieldValue(
																	fieldName([
																		'companyGroups',
																		index,
																		'managementStructure',
																	]),
																	[
																		...(value?.companyGroups?.[index]
																			.managementStructure ?? []),
																		{
																			id: newManagementId,
																			isShareholder: data.isShareholder,
																			name: data.name,
																			position: data.position,
																			shareAmount: data.shareAmount,
																			shareholderId: newShareholderId,
																			shareholding: data.shareholding,
																			shareValue: data.shareValue,
																		} as NAPCompanyGroupManagerForm,
																	],
																);

																if (!data.isShareholder) return;
																form.setFieldValue(
																	fieldName([
																		'companyGroups',
																		index,
																		'shareholderStructure',
																	]),
																	[
																		...(value?.companyGroups?.[index]
																			.shareholderStructure ?? []),
																		{
																			id: newShareholderId,
																			isManagement: true,
																			name: data.name,
																			position: data.position,
																			shareAmount: data.shareAmount,
																			shareholding: data.shareholding,
																			shareValue: data.shareValue,
																			managementId: newManagementId,
																		} as NAPCompanyGroupShareholderForm,
																	],
																);
															}}
														>
															Add Another
														</Button>
													</Col>
												</Row>
												<Table
													scroll={{ x: 'max-content' }}
													bordered
													pagination={false}
													dataSource={value?.companyGroups?.[
														index
													]?.managementStructure?.map((data, key) => {
														return {
															...data,
															key,
														};
													})}
													columns={[
														{
															title: 'Action',
															colSpan: 2,
															width: 50,
															align: 'center',
															render: (_, record, indexRecord) => (
																<a
																	style={isReadOnly ? { color: '#bababa' } : {}}
																	onClick={async (e) => {
																		e.preventDefault();
																		if (isReadOnly) return;

																		const editData =
																			await companyManagerModal.open(record);
																		console.log(editData, 123);
																		if (!editData) return;

																		form.setFieldValue(
																			fieldName([
																				'companyGroups',
																				index,
																				'managementStructure',
																			]),
																			value?.companyGroups?.[
																				index
																			].managementStructure?.map(
																				(data, dataIndex) => {
																					return indexRecord === dataIndex
																						? editData
																						: data;
																				},
																			),
																		);

																		if (!editData.isShareholder) return;
																		form.setFieldValue(
																			fieldName([
																				'companyGroups',
																				index,
																				'shareholderStructure',
																			]),
																			value?.companyGroups?.[
																				index
																			].shareholderStructure.map((data) => {
																				return data.managementId === editData.id
																					? ({
																							id: editData.shareholderId,
																							isManagement:
																								editData.isShareholder,
																							name: editData.name,
																							position: editData.position,
																							shareAmount: editData.shareAmount,
																							shareholding:
																								editData.shareholding,
																							shareValue: editData.shareValue,
																							managementId: editData.id,
																						} as NAPCompanyGroupShareholderForm)
																					: data;
																			}),
																		);
																	}}
																>
																	<EditOutlined />
																</a>
															),
														},
														{
															colSpan: 0,
															width: 50,
															align: 'center',
															render: (_, record, rowIndex) => (
																<a
																	style={isReadOnly ? { color: '#bababa' } : {}}
																	onClick={async (e) => {
																		e.preventDefault();
																		if (isReadOnly) return;

																		const isConfirmed = await modal.confirm({
																			title: 'Delete this data?',
																		});

																		if (!isConfirmed) return;

																		form.setFieldValue(
																			fieldName([
																				'companyGroups',
																				index,
																				'managementStructure',
																			]),
																			value?.companyGroups?.[
																				index
																			].managementStructure.filter(
																				(_, i) => i !== rowIndex,
																			),
																		);

																		if (!record.isShareholder) return;
																		form.setFieldValue(
																			fieldName([
																				'companyGroups',
																				index,
																				'shareholderStructure',
																			]),
																			value?.companyGroups?.[
																				index
																			].shareholderStructure.filter(
																				(data) =>
																					data.managementId !== record.id,
																			),
																		);
																	}}
																>
																	<DeleteOutlined />
																</a>
															),
														},
														{
															title: 'Nama',
															render: (_, record) => record.name,
														},
														{
															title: 'Jabatan',
															render: (_, record) => record.position?.value,
														},
													]}
												/>
												<Divider />
											</Fragment>

											<Fragment>
												<Row style={{ marginBottom: 20 }}>
													<Col flex="auto">
														<Typography.Title level={3}>
															Susunan Kepengurusan Saham
														</Typography.Title>
													</Col>
													<Col flex="none">
														<Button
															style={{
																borderRadius: 50,
																fontSize: 12,
																height: 24,
																fontWeight: 700,
																...(!isReadOnly && {
																	borderColor: '#1557BC',
																	color: '#1557BC',
																}),
															}}
															icon={'+'}
															disabled={isReadOnly}
															onClick={async () => {
																const data =
																	await companyShareholderModal.open();
																if (!data) return;

																form.setFieldValue(
																	fieldName([
																		'companyGroups',
																		index,
																		'shareholderStructure',
																	]),
																	[
																		...(value?.companyGroups?.[index]
																			.shareholderStructure ?? []),
																		data,
																	],
																);
															}}
														>
															Add Another
														</Button>
													</Col>
												</Row>
												<Table
													scroll={{ x: 'max-content' }}
													bordered
													pagination={false}
													dataSource={value?.companyGroups?.[
														index
													]?.shareholderStructure?.map((data, key) => {
														return {
															...data,
															key,
														};
													})}
													columns={[
														{
															title: 'Action',
															colSpan: 2,
															width: 50,
															align: 'center',
															render: (_, record, indexRecord) => {
																const isDisabled =
																	isReadOnly || record.isManagement;
																return (
																	<a
																		style={
																			isDisabled ? { color: '#bababa' } : {}
																		}
																		onClick={async (e) => {
																			e.preventDefault();
																			if (isDisabled) return;

																			const editData =
																				await companyShareholderModal.open(
																					record,
																				);
																			if (!editData) return;

																			form.setFieldValue(
																				fieldName([
																					'companyGroups',
																					index,
																					'shareholderStructure',
																				]),
																				value?.companyGroups?.[
																					index
																				].shareholderStructure?.map(
																					(data, dataIndex) => {
																						return indexRecord === dataIndex
																							? editData
																							: data;
																					},
																				),
																			);
																		}}
																	>
																		<EditOutlined />
																	</a>
																);
															},
														},
														{
															colSpan: 0,
															width: 50,
															align: 'center',
															render: (_, record, rowIndex) => {
																const isDisabled =
																	isReadOnly || record.managementId;
																return (
																	<a
																		style={
																			isDisabled ? { color: '#bababa' } : {}
																		}
																		onClick={async (e) => {
																			e.preventDefault();
																			if (isDisabled) return;

																			const isConfirmed = await modal.confirm({
																				title: 'Delete this data?',
																			});

																			if (!isConfirmed) return;
																			form.setFieldValue(
																				fieldName([
																					'companyGroups',
																					index,
																					'shareholderStructure',
																				]),
																				value?.companyGroups?.[
																					index
																				].shareholderStructure.filter(
																					(_, i) => i !== rowIndex,
																				),
																			);
																		}}
																	>
																		<DeleteOutlined />
																	</a>
																);
															},
														},
														{
															title: 'Nama',
															render: (_, record) => record.name,
														},
														{
															title: 'Jabatan',
															render: (_, record) => record.position?.value,
														},
														{
															title: 'Saham (Lembar)',
															render: (_, record) => record.shareAmount,
														},
														{
															title: 'Nilai (Rp)',
															render: (_, record) =>
																record.shareValue &&
																formatCurrency(record.shareValue),
														},
														{
															title: 'Porsi (%)',
															render: (_, record) =>
																record.shareholding &&
																`${record.shareholding}%`,
														},
													]}
												/>
												<Divider />
											</Fragment>
										</Fragment>
									);
								})}

								<Button
									block
									disabled={isReadOnly}
									onClick={() => control.add()}
								>
									ADD GROUP USAHA
								</Button>
							</Fragment>
						);
					}}
				</Form.List>
				<Divider />
			</Fragment>
		);
	};

	const renderCompanyTree = () => {
		return (
			<Fragment>
				<Typography.Title level={3}>Company Tree</Typography.Title>
				<Form.Item
					name={fieldName('ultimateBeneficialOwner')}
					initialValue={data?.ultimateBeneficialOwner}
					label="Ultimate Beneficial Owner"
					rules={[rule.required()]}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>

				<Divider />

				<Form.Item
					name={fieldName('exposureDocument')}
					initialValue={data?.exposureDocument}
					label="One Obligor/Konsolidasi Exposure"
					rules={[rule.required()]}
					getValueProps={(value) => {
						const file = value as FileData;
						return {
							fileList: file?.id
								? ([
										{
											uid: file.id,
											name: file.name,
											thumbUrl: file.url,
										},
									] as UploadFile[])
								: [],
						};
					}}
					normalize={({ file }: { file: UploadFile }) => {
						return {
							id: file.uid,
							format: file.type,
							name: file.name,
							url: file.thumbUrl,
						} as FileData;
					}}
				>
					<Upload.Dragger
						disabled={isReadOnly}
						style={{ width: 260 }}
						listType="picture"
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						beforeUpload={() => false}
						onRemove={() => {
							form.setFieldValue(fieldName('exposureDocument'), null);
							return false;
						}}
						onChange={async ({ file }) => {
							const fileid = Date.now().toString();
							const filepath = `customer/${customerId}/${prospectId}/nap`;

							try {
								const url = await uploadMutation.mutateAsync({
									file: file as unknown as File,
									fileid,
									filepath,
								});
								form.setFieldValue(fieldName('exposureDocument'), {
									format: 'jpeg',
									id: fileid,
									name: file.name,
									path: filepath,
									url,
								});
							} catch {
								form.setFieldValue(fieldName('exposureDocument'), null);
							}
						}}
					>
						<UploadOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					</Upload.Dragger>
				</Form.Item>

				<Divider />

				<Form.Item
					name={fieldName('companyTreeDocument')}
					initialValue={data?.companyTreeDocument}
					label="Upload Company Tree"
					rules={[rule.required()]}
					getValueProps={(value) => {
						const file = value as FileData;
						return {
							fileList: file?.id
								? ([
										{
											uid: file.id,
											name: file.name,
											thumbUrl: file.url,
										},
									] as UploadFile[])
								: [],
						};
					}}
					normalize={({ file }: { file: UploadFile }) => {
						return {
							id: file.uid,
							format: file.type,
							name: file.name,
							url: file.thumbUrl,
						} as FileData;
					}}
				>
					<Upload.Dragger
						disabled={isReadOnly}
						style={{ width: 260 }}
						listType="picture"
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						beforeUpload={() => false}
						onRemove={() => {
							form.setFieldValue(fieldName('companyTreeDocument'), null);
							return false;
						}}
						onChange={async ({ file }) => {
							const fileid = Date.now().toString();
							const filepath = `customer/${customerId}/${prospectId}/nap`;

							try {
								const url = await uploadMutation.mutateAsync({
									file: file as unknown as File,
									fileid,
									filepath,
								});
								form.setFieldValue(fieldName('companyTreeDocument'), {
									format: 'jpeg',
									id: fileid,
									name: file.name,
									path: filepath,
									url,
								});
							} catch {
								form.setFieldValue(fieldName('companyTreeDocument'), null);
							}
						}}
					>
						<UploadOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					</Upload.Dragger>
				</Form.Item>

				<Divider />
			</Fragment>
		);
	};

	const renderBackground = () => {
		return (
			<Fragment>
				<Typography.Title level={3}>
					Latar Belakang dan Sejarah Perusahaan
				</Typography.Title>

				<Form.Item
					name={fieldName('companyBackground')}
					initialValue={data?.companyBackground}
					label="Latar Belakang Perusahaan"
					rules={[rule.required()]}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>

				<Divider />

				<Form.Item
					name={fieldName('yearlyBusinessDevelopment')}
					initialValue={data?.yearlyBusinessDevelopment}
					label="Perkembangan Bisnis dari Tahun ke Tahun"
					rules={[rule.required()]}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>

				<Divider />

				<Form.Item
					name={fieldName('notes')}
					initialValue={data?.notes}
					label={groupName === 'FRM' ? 'Informasi Tambahan' : 'Notes FRM'}
					rules={groupName === 'FRM' ? [rule.required()] : []}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly || groupName !== 'FRM'}
					/>
				</Form.Item>
				<Divider />
				{groupName !== 'FRM' && (
					<Form.Item
						label={groupName === 'CCA' ? 'Verification Notes' : 'Notes CCA'}
						style={{ marginTop: 20 }}
						name={'verificationNotes'}
						initialValue={data?.verificationNotes}
						rules={groupName === 'CCA' ? [rule.required()] : []}
					>
						<TextArea
							rows={3}
							disabled={isReadOnly || groupName !== 'CCA'}
						/>
					</Form.Item>
				)}
			</Fragment>
		);
	};

	return (
		<Fragment>
			<ShareholderModal {...shareholderModal} />
			<ManagerModal {...managerModal} />
			<GeneralInfoModal {...generalInfoModalModal} />
			<ManagerDetailModal {...managerDetailModal} />
			<ShareholderDetailModal {...shareholderDetailModal} />
			<CompanyManagerModal {...companyManagerModal} />
			<CompanyShareholderModal {...companyShareholderModal} />
			<UploadModal
				customerId={customerId}
				prospectId={prospectId}
				{...uplodaModal}
			/>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				labelCol={{
					span: 8,
				}}
				wrapperCol={{
					span: 16,
				}}
				onFinish={async (values) => {
					const payload: NAPCompanyInformationPayload = {
						id: values.id,
						napId: values.napId,
						prospectId: values.prospectId,
						relationshipYear: values.relationshipYear,
						financedSectorBusinessStart: values.financedSectorBusinessStart,
						numberOfEmployees: values.numberOfEmployees,
						bmppId: values.bmppId,
						ultimateBeneficialOwner: values.ultimateBeneficialOwner,
						exposureDocument: values.exposureDocument && {
							id: values.exposureDocument.id,
							name: values.exposureDocument.name,
							path: values.exposureDocument.path,
						},
						companyTreeDocument: values.companyTreeDocument && {
							id: values.companyTreeDocument.id,
							name: values.companyTreeDocument.name,
							path: values.companyTreeDocument.path,
						},
						companyBackground: values.companyBackground,
						yearlyBusinessDevelopment: values.yearlyBusinessDevelopment,
						companyManagements: values.companyManagements.map((data) => {
							return {
								id: data.id,
								name: data.name,
								citizenship: data.citizenship?.id,
								nationalityId: data.nationality?.id,
								identityCardType: data.identityCardType?.id,
								identityNumber: data.identityNumber,
								address: data.address,
								rt: data.rt,
								rw: data.rw,
								postalId: data.postal?.postalId,
								phoneNumber: data.phoneNumber,
								identityFile: data.identityFile && {
									id: data.identityFile.id,
									name: data.identityFile.name,
									path: data.identityFile.path,
								},
								dateOfBirth: data.dateOfBirth,
								position: data.positionId?.id ?? 0,
								status: data.status?.id,
								isShareholder: data?.isShareholder ?? false,
								shareholding: data?.shareholding ?? 0,
								shareholderId: data?.shareholderId ?? undefined,
								customerId: undefined as unknown as string,
							};
						}),
						companyShareholders: values.companyShareholders
							.filter((data) => !data.isManagement)
							.map((data) => {
								return {
									id: data.id,
									name: data.name,
									address: data.address,
									citizenship: data.citizenship?.id,
									identityCardType: data.identityCardType?.id,
									identityFile: data.identityFile && {
										id: data.identityFile.id,
										name: data.identityFile.name,
										path: data.identityFile.path,
									},
									identityNumber: data.identityNumber,
									nationalityId: data.nationality?.id,
									phoneNumber: data.phoneNumber,
									postalId: data.postal?.postalId,
									rt: data.rt,
									rw: data.rw,
									shareholderType: data.shareholderType?.id,
									status: data.status?.id,
									shareholding: data.shareholding,
									customerId: undefined as unknown as string,
								};
							}),
						companyGroups: values.companyGroups.map((data) => {
							return {
								generalInformation: {
									id: data.generalInformation?.id,
									entityName: data.generalInformation?.entityName,
									keyPerson: data.generalInformation?.keyPerson,
									relationshipYear: data.generalInformation?.relationshipYear,
									industrySectorId: data.generalInformation?.industrySector?.id,
								},
								isDefault: data.isDefault ?? false,
								managementStructure: data.managementStructure?.map(
									(manager) => {
										return {
											id: manager.id && manager.id < 0 ? null! : manager.id,
											name: manager.name,
											position: manager.position?.id ?? undefined,
											isShareholder: manager.isShareholder,
											shareholding: manager.shareholding,
											shareAmount: manager.shareAmount,
											shareValue: manager.shareValue,
										};
									},
								),
								shareholderStructure: data.shareholderStructure
									?.filter((holder) => !holder.isManagement)
									.map((holder) => {
										return {
											id: holder.id,
											name: holder.name,
											position: holder.position?.id ?? undefined,
											shareholding: holder.shareholding,
											shareAmount: holder.shareAmount,
											shareValue: holder.shareValue,
										};
									}),
							};
						}),
						notes: values.notes,
						latestShareholderDeedNumber:
							values.latestShareholderDeedNumber &&
							String(values.latestShareholderDeedNumber),
						latestShareholderDeedYear: values.latestShareholderDeedYear,
						latestShareholderDeedDocument:
							values.latestShareholderDeedDocument && {
								id: values.latestShareholderDeedDocument.id,
								name: values.latestShareholderDeedDocument.name,
								path: values.latestShareholderDeedDocument.path,
							},
						latestManagementDeedNumber:
							values.latestManagementDeedNumber &&
							String(values.latestManagementDeedNumber),
						latestManagementDeedYear: values.latestManagementDeedYear,
						latestManagementDeedDocument:
							values.latestManagementDeedDocument && {
								id: values.latestManagementDeedDocument.id,
								name: values.latestManagementDeedDocument.name,
								path: values.latestManagementDeedDocument.path,
							},
					};

					if (groupName === 'FRM') {
						await submitNAPCompanyInfoMutation.mutateAsync(payload);
					}

					if (groupName === 'CCA') {
						const verificationNotes =
							'verificationNotes' in values
								? (values.verificationNotes as string)
								: '';

						await verifyNAPCompanyInfoMutation.mutateAsync({
							...payload,
							verificationNotes,
						});
					}
					onSubmitted();
				}}
			>
				<Form.Item
					name={fieldName('prospectId')}
					initialValue={prospectId}
					noStyle
				/>
				<Form.Item
					name={fieldName('napId')}
					initialValue={data?.napId}
					noStyle
				/>
				<Form.Item
					name={fieldName('id')}
					initialValue={data?.id}
					noStyle
				/>
				<Form.Item
					name={fieldName('companyManagements')}
					initialValue={data?.companyManagements ?? []}
					noStyle
				/>
				<Form.Item
					name={fieldName('companyShareholders')}
					initialValue={data?.companyShareholders ?? []}
					noStyle
				/>
				<Form.Item
					name={fieldName('latestManagementDeedDocument')}
					// initialValue={data?.latestManagementDeedDocument}
					initialValue={undefined}
					noStyle
				/>
				<Form.Item
					name={fieldName('latestShareholderDeedDocument')}
					// initialValue={data?.latestShareholderDeedDocument}
					initialValue={undefined}
					noStyle
				/>
				{renderCompanyInfo()}
				{renderGeneralGroup()}
				{renderCompanyTree()}
				{renderBackground()}
				<Button
					type="primary"
					htmlType="submit"
					style={{
						width: 200,
						display: 'block',
						marginLeft: 'auto',
						marginTop: 40,
					}}
					disabled={
						submitNAPCompanyInfoMutation.isPending ||
						verifyNAPCompanyInfoMutation.isPending ||
						isReadOnly
					}
				>
					NEXT
				</Button>
			</Form>
		</Fragment>
	);
};

export default NAPCompanyGroup;
